/**
 * Formats a number as a percentage string
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string with % symbol
 */
export const formatPercent = (value: number, decimals: number = 2): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Formats a number as a currency string
 * @param value - The number to format
 * @returns Formatted currency string with appropriate suffix (K, M, B, T)
 */
export const formatCurrency = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  const absValue = Math.abs(value);
  
  if (absValue >= 1e12) {
    return `$${(value / 1e12).toFixed(1)}T`;
  }
  if (absValue >= 1e9) {
    return `$${(value / 1e9).toFixed(1)}B`;
  }
  if (absValue >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (absValue >= 1e3) {
    return `$${(value / 1e3).toFixed(1)}K`;
  }
  
  return `$${value.toFixed(2)}`;
};

/**
 * Formats a large number with appropriate suffix
 * @param value - The number to format
 * @returns Formatted string with appropriate suffix (K, M, B, T)
 */
export const formatNumber = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  const absValue = Math.abs(value);
  
  if (absValue >= 1e12) {
    return `${(value / 1e12).toFixed(1)}T`;
  }
  if (absValue >= 1e9) {
    return `${(value / 1e9).toFixed(1)}B`;
  }
  if (absValue >= 1e6) {
    return `${(value / 1e6).toFixed(1)}M`;
  }
  if (absValue >= 1e3) {
    return `${(value / 1e3).toFixed(1)}K`;
  }
  
  return value.toLocaleString();
};

/**
 * Formats a date string to a more readable format
 * @param dateStr - The date string to format
 * @returns Formatted date string
 */
export const formatDate = (dateStr: string): string => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}; 